import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  User,
  CreditCard,
  Globe,
  Bell,
  Shield,
  Palette,
  Database,
  HelpCircle,
  LogOut,
  Camera,
  Edit2,
  Save,
  X,
  Check,
  Zap,
  Mail,
  Phone,
  Moon,
  Sun,
  Monitor,
  AlertTriangle,
  Download,
  Trash2,
  Smartphone,
  RefreshCw,
  EyeOff,
  Copy,
  Link,
  Trash,
} from 'lucide-react';
import { userService, settingsService } from '../../api/services';
import { useCredits } from '../../contexts/CreditsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLang } from '../../i18n/LanguageContext';

export default function Settings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const { balance: creditsBalance } = useCredits();
  const { theme, setTheme } = useTheme();
  const { t, lang, setLang } = useLang();
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');
  const [userData, setUserData] = useState(null);
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);

  const ts = t.settings;

  const tabs = [
    { id: 'profile', label: ts.tabs.profile, icon: User },
    { id: 'subscription', label: ts.tabs.subscription, icon: CreditCard },
    { id: 'preferences', label: ts.tabs.preferences, icon: Globe },
    { id: 'notifications', label: ts.tabs.notifications, icon: Bell },
    { id: 'security', label: ts.tabs.security, icon: Shield },
    { id: 'sms-gateway', label: ts.tabs.smsGateway, icon: Smartphone },
    { id: 'appearance', label: ts.tabs.appearance, icon: Palette },
    { id: 'data', label: ts.tabs.data, icon: Database },
    { id: 'help', label: ts.tabs.help, icon: HelpCircle }
  ];

  // Gérer l'onglet depuis les paramètres URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabs.find(tab => tab.id === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // États pour les différentes sections
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    avatar: null,
  });

  const [preferences, setPreferences] = useState({
    language: 'fr',
    theme: 'dark',
    notifications: {
      email: true,
      push: true,
      sms: false,
      desktop: true
    },
    privacy: {
      shareData: false,
      analytics: true,
      marketing: false
    }
  });

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [tempProfileData, setTempProfileData] = useState(profileData);

  // ── Passerelle SMS ──────────────────────────────────────────────────────────
  const [gatewayInfo, setGatewayInfo] = useState(null);    // { hasToken, maskedToken, webhookUrl }
  const [gatewayLoading, setGatewayLoading] = useState(false);
  const [newToken, setNewToken] = useState(null);           // affiché UNE seule fois après génération
  const [tokenCopied, setTokenCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  const loadGatewayInfo = useCallback(async () => {
    try {
      const res = await settingsService.getGatewayTokenInfo();
      setGatewayInfo(res.data);
    } catch {
      setGatewayInfo({ hasToken: false });
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'sms-gateway' && gatewayInfo === null) {
      loadGatewayInfo();
    }
  }, [activeTab, gatewayInfo, loadGatewayInfo]);

  const handleGenerateToken = async () => {
    if (gatewayInfo?.hasToken) {
      if (!window.confirm(ts.smsGateway.regenerateConfirm)) return;
    }
    setGatewayLoading(true);
    setNewToken(null);
    try {
      const res = await settingsService.generateGatewayToken();
      setNewToken(res.data.token);
      await loadGatewayInfo();
      setSaveSuccess(ts.smsGateway.generateSuccess);
    } catch {
      setSaveError(ts.smsGateway.generateError);
    } finally {
      setGatewayLoading(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!window.confirm(ts.smsGateway.revokeConfirm)) return;
    setGatewayLoading(true);
    try {
      await settingsService.revokeGatewayToken();
      setNewToken(null);
      await loadGatewayInfo();
      setSaveSuccess(ts.smsGateway.revokeSuccess);
    } catch {
      setSaveError(ts.smsGateway.revokeError);
    } finally {
      setGatewayLoading(false);
    }
  };

  const copyToClipboard = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch {
      /* clipboard non disponible */
    }
  };

  // Seules FR/EN sont supportées dans le contexte i18n (ES/DE/IT/PT affichées mais désactivées)
  const languages = [
    { code: 'fr', name: 'Français', flag: '🇫🇷', supported: true },
    { code: 'en', name: 'English', flag: '🇬🇧', supported: true },
    { code: 'es', name: 'Español', flag: '🇪🇸', supported: false },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', supported: false },
    { code: 'it', name: 'Italiano', flag: '🇮🇹', supported: false },
    { code: 'pt', name: 'Português', flag: '🇵🇹', supported: false },
  ];

  // Charger les données utilisateur au montage
  useEffect(() => {
    loadUserData();
    loadActiveSessions();
  }, []);

  const loadUserData = async () => {
    // Hydratation immédiate depuis le cache localStorage
    const cached = localStorage.getItem('user');
    if (cached) {
      const u = JSON.parse(cached);
      setProfileData({
        firstName: u.firstName || '',
        lastName: u.lastName || '',
        email: u.email || '',
        avatar: u.avatarUrl || null,
      });
    }

    // Rafraîchissement depuis /auth/me
    try {
      // getProfile() retourne response.data = { success, data: { id, email, ... } }
      const res = await userService.getProfile();
      const u = res.data;
      setUserData(u);
      setProfileData({
        firstName: u.firstName || '',
        lastName: u.lastName || '',
        email: u.email || '',
        avatar: u.avatarUrl || null,
      });
      // Sync localStorage
      const base = cached ? JSON.parse(cached) : {};
      localStorage.setItem('user', JSON.stringify({
        ...base,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        avatarUrl: u.avatarUrl,
      }));
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
    }
  };

  const loadActiveSessions = async () => {
    try {
      const sessions = await userService.getActiveSessions();
      setActiveSessions(sessions);
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
      // Sessions de démonstration si l'API n'est pas disponible
      setActiveSessions([
        {
          id: '1',
          device: 'Chrome - Windows',
          location: 'Paris, France',
          lastActive: 'Actif maintenant',
          isCurrent: true
        },
        {
          id: '2',
          device: 'Safari - iPhone',
          location: 'Lyon, France',
          lastActive: 'Il y a 2 heures',
          isCurrent: false
        }
      ]);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setSaveError('');
    setSaveSuccess('');

    try {
      // Le validator backend n'accepte que firstName, lastName, avatarUrl
      await userService.updateProfile({
        firstName: tempProfileData.firstName,
        lastName: tempProfileData.lastName,
        ...(tempProfileData.avatar ? { avatarUrl: tempProfileData.avatar } : {}),
      });
      setProfileData(tempProfileData);
      setIsEditingProfile(false);
      setSaveSuccess(ts.profile.saved);

      // Sync localStorage
      const cached = localStorage.getItem('user');
      if (cached) {
        const u = JSON.parse(cached);
        localStorage.setItem('user', JSON.stringify({
          ...u,
          firstName: tempProfileData.firstName,
          lastName: tempProfileData.lastName,
          avatarUrl: tempProfileData.avatar,
        }));
      }

      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      setSaveError(ts.profile.error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setSaveError(ts.security.passwordMismatch);
      return;
    }

    setLoading(true);
    setSaveError('');
    setSaveSuccess('');

    try {
      await userService.changePassword({
        currentPassword: changePasswordData.currentPassword,
        newPassword: changePasswordData.newPassword
      });
      setSaveSuccess(ts.security.passwordSuccess);
      setChangePasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      setSaveError(ts.security.passwordError);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle2FA = async () => {
    try {
      if (twoFactorEnabled) {
        await userService.disable2FA();
        setTwoFactorEnabled(false);
        setSaveSuccess(ts.security.twoFADisabledSuccess);
      } else {
        // Rediriger vers la page de configuration 2FA
        navigate('/settings/2fa-setup');
      }
    } catch (error) {
      console.error('Erreur lors de la gestion 2FA:', error);
      setSaveError(ts.security.twoFAError);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      await userService.revokeSession(sessionId);
      setActiveSessions(activeSessions.filter(session => session.id !== sessionId));
      setSaveSuccess(ts.security.sessionRevokeSuccess);
    } catch (error) {
      console.error('Erreur lors de la révocation de session:', error);
      setSaveError(ts.security.sessionRevokeError);
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      const data = await userService.exportData();
      // Créer un blob et télécharger
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `swift-ai-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSaveSuccess(ts.data.exportSuccess);
    } catch (error) {
      console.error("Erreur lors de l'export des données:", error);
      setSaveError(ts.data.exportError);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm(ts.data.deleteConfirm1)) {
      if (window.confirm(ts.data.deleteConfirm2)) {
        setLoading(true);
        try {
          await userService.deleteAccount();
          // Déconnexion et redirection
          localStorage.removeItem('token');
          navigate('/login');
        } catch (error) {
          console.error('Erreur lors de la suppression du compte:', error);
          setSaveError(ts.data.deleteError);
          setLoading(false);
        }
      }
    }
  };

  const handleLogout = async () => {
    try {
      await userService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  // Messages de succès/erreur
  useEffect(() => {
    if (saveSuccess || saveError) {
      const timer = setTimeout(() => {
        setSaveSuccess('');
        setSaveError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess, saveError]);

  return (
    <div>
      <div className="max-w-7xl mx-auto px-0 sm:px-6 py-4 sm:py-8">
        {/* En-tête de page */}
        <div className="mb-6">
          <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {ts.title}
          </p>
          <h1 className="text-ink font-semibold" style={{ fontSize: '18px' }}>{ts.subtitle}</h1>
        </div>

        {/* Messages */}
        {saveSuccess && (
          <div className="mb-6 rounded-lg p-4 flex items-center gap-3" style={{ background: 'rgba(79,179,103,0.1)', border: '0.5px solid rgba(79,179,103,0.3)' }}>
            <Check style={{ color: 'var(--c-success)' }} size={18} />
            <span className="text-sm" style={{ color: 'var(--c-success)' }}>{saveSuccess}</span>
          </div>
        )}

        {saveError && (
          <div className="mb-6 rounded-lg p-4 flex items-center gap-3" style={{ background: 'rgba(224,84,84,0.1)', border: '0.5px solid rgba(224,84,84,0.3)' }}>
            <X style={{ color: 'var(--c-error)' }} size={18} />
            <span className="text-sm" style={{ color: 'var(--c-error)' }}>{saveError}</span>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <div className="card p-2 flex lg:flex-col gap-1 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="flex-shrink-0 lg:w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all whitespace-nowrap"
                    style={{
                      background: isActive ? 'rgb(var(--c-accent-green-rgb))' : 'transparent',
                      color: isActive ? '#fff' : 'var(--c-text-gray)',
                    }}
                  >
                    <Icon size={18} />
                    <span className="text-sm">{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Logout button */}
            <button
              onClick={handleLogout}
              className="w-full mt-4 flex items-center gap-3 px-4 py-3 rounded-lg text-red-500 hover:bg-red-500/10 transition-all"
            >
              <LogOut size={20} />
              <span className="text-sm">{ts.logout}</span>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Profil */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="card p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                    <h2 className="text-ink font-semibold text-lg">{ts.profile.title}</h2>
                    {!isEditingProfile ? (
                      <button
                        onClick={() => {
                          setIsEditingProfile(true);
                          setTempProfileData(profileData);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-accent-green text-black rounded-lg font-medium hover:bg-accent-green/80 transition-all"
                      >
                        <Edit2 size={16} />
                        {t.common.edit}
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setIsEditingProfile(false)}
                          className="px-4 py-2 text-ink rounded-lg font-medium transition-all" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                        >
                          {t.common.cancel}
                        </button>
                        <button
                          onClick={handleSaveProfile}
                          disabled={loading}
                          className="flex items-center gap-2 px-4 py-2 bg-accent-green text-black rounded-lg font-medium hover:bg-accent-green/80 transition-all disabled:opacity-50"
                        >
                          {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                          ) : (
                            <Save size={16} />
                          )}
                          {t.common.save}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Avatar section */}
                  <div className="flex items-center gap-6 mb-8">
                    <div className="relative">
                      {profileData.avatar ? (
                        <img 
                          src={profileData.avatar} 
                          alt="Avatar" 
                          className="w-24 h-24 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-accent-green/20 rounded-full flex items-center justify-center">
                          <User className="text-accent-green" size={40} />
                        </div>
                      )}
                      {isEditingProfile && (
                        <>
                          <input
                            type="file"
                            id="avatar-upload"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                try {
                                  const response = await userService.uploadAvatar(file);
                                  setTempProfileData({ ...tempProfileData, avatar: response.url });
                                  setSaveSuccess(ts.profile.avatarSuccess);
                                } catch (error) {
                                  setSaveError(ts.profile.avatarError);
                                }
                              }
                            }}
                            className="hidden"
                          />
                          <label htmlFor="avatar-upload" className="absolute bottom-0 right-0 w-8 h-8 bg-accent-green rounded-full flex items-center justify-center text-black cursor-pointer hover:bg-accent-green/80 transition-all">
                            <Camera size={16} />
                          </label>
                        </>
                      )}
                    </div>
                    <div>
                      <h3 className="text-ink font-semibold">
                        {`${profileData.firstName} ${profileData.lastName}`.trim() || '—'}
                      </h3>
                      <p className="text-label">{profileData.email}</p>
                    </div>
                  </div>

                  {/* Form fields — seuls les champs persistés en base */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-ink mb-2">{ts.profile.firstName}</label>
                      <input
                        type="text"
                        value={isEditingProfile ? tempProfileData.firstName : profileData.firstName}
                        onChange={(e) => setTempProfileData({ ...tempProfileData, firstName: e.target.value })}
                        disabled={!isEditingProfile}
                        className="w-full text-ink px-4 py-3 rounded-lg outline-none disabled:opacity-50" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink mb-2">{ts.profile.lastName}</label>
                      <input
                        type="text"
                        value={isEditingProfile ? tempProfileData.lastName : profileData.lastName}
                        onChange={(e) => setTempProfileData({ ...tempProfileData, lastName: e.target.value })}
                        disabled={!isEditingProfile}
                        className="w-full text-ink px-4 py-3 rounded-lg outline-none disabled:opacity-50" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-ink mb-2">
                        {ts.profile.email} <span className="text-label text-xs font-normal">{ts.profile.emailNote}</span>
                      </label>
                      <input
                        type="email"
                        value={profileData.email}
                        disabled
                        className="w-full text-ink px-4 py-3 rounded-lg opacity-50 cursor-not-allowed" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Abonnement */}
            {activeTab === 'subscription' && (
              <div className="space-y-6">
                {/* Crédits (source réelle : CreditsContext) */}
                <div className="card p-4 sm:p-6">
                  <h2 className="text-ink font-semibold text-lg mb-6">{ts.subscription.creditsTitle}</h2>
                  <div className="flex items-center justify-between p-6 bg-accent-green/5 border border-accent-green/30 rounded-lg">
                    <div>
                      <p className="text-label text-sm mb-1">{ts.subscription.currentBalance}</p>
                      <p className="text-4xl font-extrabold text-accent-green">
                        {creditsBalance !== null ? creditsBalance.toLocaleString('fr-FR') : '—'}
                      </p>
                      <p className="text-label text-xs mt-1">{ts.subscription.aiCredits}</p>
                    </div>
                    <Zap className="text-accent-green opacity-30" size={64} />
                  </div>
                </div>

                {/* Plans — à venir */}
                <div className="card p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-ink font-semibold text-lg">{ts.subscription.billingTitle}</h2>
                    <span className="text-xs font-bold bg-accent-green/10 text-accent-green border border-accent-green/30 px-3 py-1 rounded-full">
                      {ts.subscription.comingSoon}
                    </span>
                  </div>
                  <p className="text-label mb-6">
                    {ts.subscription.comingSoonText}
                  </p>
                  <button
                    onClick={() => window.open('/pricing', '_blank')}
                    className="px-6 py-3 bg-accent-green text-black rounded-lg font-semibold hover:bg-accent-green/80 transition-all"
                  >
                    {ts.subscription.seePricing}
                  </button>
                </div>
              </div>
            )}

            {/* Préférences */}
            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <div className="card p-4 sm:p-6">
                  <h2 className="text-ink font-semibold text-lg mb-6">{ts.preferences.title}</h2>

                  {/* Langue */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-ink mb-3">{ts.preferences.language}</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {languages.map((l) => (
                        <button
                          key={l.code}
                          onClick={() => {
                            if (!l.supported) return;
                            setLang(l.code);
                            setPreferences({ ...preferences, language: l.code });
                            setSaveSuccess(ts.preferences.langSuccess);
                          }}
                          disabled={!l.supported}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                            lang === l.code
                              ? 'border-accent-green bg-accent-green/10'
                              : 'border-primary-dark hover:border-accent-green/50'
                          } ${!l.supported ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          <span className="text-2xl">{l.flag}</span>
                          <span className="text-ink text-sm">{l.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Fuseau horaire */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-ink mb-3">{ts.preferences.timezone}</label>
                    <select
                      value="Europe/Paris"
                      className="w-full text-ink px-4 py-3 rounded-lg outline-none" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                    >
                      <option>Europe/Paris</option>
                      <option>America/New_York</option>
                      <option>Asia/Tokyo</option>
                      <option>Europe/London</option>
                    </select>
                  </div>

                  {/* Format de date */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-ink mb-3">{ts.preferences.dateFormat}</label>
                    <div className="grid grid-cols-3 gap-3">
                      <button className="p-3 text-ink rounded-lg transition-all" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                        JJ/MM/AAAA
                      </button>
                      <button className="p-3 text-ink rounded-lg transition-all" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                        MM/JJ/AAAA
                      </button>
                      <button className="p-3 text-ink rounded-lg transition-all" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                        AAAA-MM-JJ
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="card p-4 sm:p-6">
                  <h2 className="text-ink font-semibold text-lg mb-6">{ts.notifications.title}</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg" style={{ background: 'var(--c-hover)' }}>
                      <div className="flex items-center gap-3">
                        <Mail className="text-accent-green" size={20} />
                        <div>
                          <p className="text-ink font-medium">{ts.notifications.email}</p>
                          <p className="text-label text-sm">{ts.notifications.emailDesc}</p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await userService.updateNotifications({
                              ...preferences.notifications,
                              email: !preferences.notifications.email
                            });
                            setPreferences({
                              ...preferences,
                              notifications: { ...preferences.notifications, email: !preferences.notifications.email }
                            });
                            setSaveSuccess(ts.notifications.updateSuccess);
                          } catch (error) {
                            setSaveError(ts.notifications.updateError);
                          }
                        }}
                        className={`w-12 h-6 rounded-full transition-all ${
                          preferences.notifications.email ? 'bg-accent-green' : ''
                        }`}
                        style={!preferences.notifications.email ? { background: '#6B7280' } : {}}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full m-0.5 transition-all ${
                          preferences.notifications.email ? 'translate-x-6' : 'translate-x-0'
                        }`}></div>
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg" style={{ background: 'var(--c-hover)' }}>
                      <div className="flex items-center gap-3">
                        <Bell className="text-accent-green" size={20} />
                        <div>
                          <p className="text-ink font-medium">{ts.notifications.push}</p>
                          <p className="text-label text-sm">{ts.notifications.pushDesc}</p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await userService.updateNotifications({
                              ...preferences.notifications,
                              push: !preferences.notifications.push
                            });
                            setPreferences({
                              ...preferences,
                              notifications: { ...preferences.notifications, push: !preferences.notifications.push }
                            });
                            setSaveSuccess(ts.notifications.updateSuccess);
                          } catch (error) {
                            setSaveError(ts.notifications.updateError);
                          }
                        }}
                        className={`w-12 h-6 rounded-full transition-all ${
                          preferences.notifications.push ? 'bg-accent-green' : ''
                        }`}
                        style={!preferences.notifications.push ? { background: '#6B7280' } : {}}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full m-0.5 transition-all ${
                          preferences.notifications.push ? 'translate-x-6' : 'translate-x-0'
                        }`}></div>
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg" style={{ background: 'var(--c-hover)' }}>
                      <div className="flex items-center gap-3">
                        <Phone className="text-accent-green" size={20} />
                        <div>
                          <p className="text-ink font-medium">{ts.notifications.sms}</p>
                          <p className="text-label text-sm">{ts.notifications.smsDesc}</p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await userService.updateNotifications({
                              ...preferences.notifications,
                              sms: !preferences.notifications.sms
                            });
                            setPreferences({
                              ...preferences,
                              notifications: { ...preferences.notifications, sms: !preferences.notifications.sms }
                            });
                            setSaveSuccess(ts.notifications.updateSuccess);
                          } catch (error) {
                            setSaveError(ts.notifications.updateError);
                          }
                        }}
                        className={`w-12 h-6 rounded-full transition-all ${
                          preferences.notifications.sms ? 'bg-accent-green' : ''
                        }`}
                        style={!preferences.notifications.sms ? { background: '#6B7280' } : {}}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full m-0.5 transition-all ${
                          preferences.notifications.sms ? 'translate-x-6' : 'translate-x-0'
                        }`}></div>
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg" style={{ background: 'var(--c-hover)' }}>
                      <div className="flex items-center gap-3">
                        <Monitor className="text-accent-green" size={20} />
                        <div>
                          <p className="text-ink font-medium">{ts.notifications.desktop}</p>
                          <p className="text-label text-sm">{ts.notifications.desktopDesc}</p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await userService.updateNotifications({
                              ...preferences.notifications,
                              desktop: !preferences.notifications.desktop
                            });
                            setPreferences({
                              ...preferences,
                              notifications: { ...preferences.notifications, desktop: !preferences.notifications.desktop }
                            });
                            setSaveSuccess(ts.notifications.updateSuccess);
                          } catch (error) {
                            setSaveError(ts.notifications.updateError);
                          }
                        }}
                        className={`w-12 h-6 rounded-full transition-all ${
                          preferences.notifications.desktop ? 'bg-accent-green' : ''
                        }`}
                        style={!preferences.notifications.desktop ? { background: '#6B7280' } : {}}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full m-0.5 transition-all ${
                          preferences.notifications.desktop ? 'translate-x-6' : 'translate-x-0'
                        }`}></div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sécurité */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div className="card p-4 sm:p-6">
                  <h2 className="text-ink font-semibold text-lg mb-6">{ts.security.title}</h2>
                  
                  <div className="space-y-6">
                    {/* Mot de passe */}
                    <div>
                      <h3 className="text-ink font-semibold mb-4">{ts.security.passwordTitle}</h3>
                      {!showPasswordForm ? (
                        <button
                          onClick={() => setShowPasswordForm(true)}
                          className="px-4 py-2 bg-accent-green text-black rounded-lg font-medium hover:bg-accent-green/80 transition-all"
                        >
                          {ts.security.changePassword}
                        </button>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-ink mb-2">{ts.security.currentPassword}</label>
                            <input
                              type="password"
                              value={changePasswordData.currentPassword}
                              onChange={(e) => setChangePasswordData({ ...changePasswordData, currentPassword: e.target.value })}
                              className="w-full text-ink px-4 py-3 rounded-lg outline-none" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                              placeholder={ts.security.currentPasswordPlaceholder}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-ink mb-2">{ts.security.newPassword}</label>
                            <input
                              type="password"
                              value={changePasswordData.newPassword}
                              onChange={(e) => setChangePasswordData({ ...changePasswordData, newPassword: e.target.value })}
                              className="w-full text-ink px-4 py-3 rounded-lg outline-none" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                              placeholder={ts.security.newPasswordPlaceholder}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-ink mb-2">{ts.security.confirmPassword}</label>
                            <input
                              type="password"
                              value={changePasswordData.confirmPassword}
                              onChange={(e) => setChangePasswordData({ ...changePasswordData, confirmPassword: e.target.value })}
                              className="w-full text-ink px-4 py-3 rounded-lg outline-none" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                              placeholder={ts.security.confirmPasswordPlaceholder}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={handleChangePassword}
                              disabled={loading}
                              className="px-4 py-2 bg-accent-green text-black rounded-lg font-medium hover:bg-accent-green/80 transition-all disabled:opacity-50"
                            >
                              {loading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                              ) : (
                                ts.security.update
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setShowPasswordForm(false);
                                setChangePasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                              }}
                              className="px-4 py-2 text-ink rounded-lg font-medium transition-all" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                            >
                              {t.common.cancel}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Authentification 2FA */}
                    <div>
                      <h3 className="text-ink font-semibold mb-4">{ts.security.twoFATitle}</h3>
                      <div className="flex items-center justify-between p-4 rounded-lg" style={{ background: 'var(--c-hover)' }}>
                        <div>
                          <p className="text-ink font-medium">{twoFactorEnabled ? ts.security.twoFAEnabled : ts.security.twoFADisabled}</p>
                          <p className="text-label text-sm">
                            {twoFactorEnabled ? ts.security.twoFAProtected : ts.security.twoFAAdd}
                          </p>
                        </div>
                        <button
                          onClick={handleToggle2FA}
                          className={`px-4 py-2 rounded-lg font-medium transition-all ${
                            twoFactorEnabled
                              ? 'bg-red-500 text-white hover:bg-red-600'
                              : 'bg-accent-green text-black hover:opacity-80'
                          }`}
                        >
                          {twoFactorEnabled ? ts.security.disable : ts.security.enable}
                        </button>
                      </div>
                    </div>

                    {/* Sessions actives */}
                    <div>
                      <h3 className="text-ink font-semibold mb-4">{ts.security.sessionsTitle}</h3>
                      <div className="space-y-3">
                        {activeSessions.map((session) => (
                          <div key={session.id} className="flex items-center justify-between p-4 rounded-lg" style={{ background: 'var(--c-hover)' }}>
                            <div className="flex items-center gap-3">
                              <Monitor className={session.isCurrent ? 'text-accent-green' : 'text-label'} size={20} />
                              <div>
                                <p className="text-ink">{session.device}</p>
                                <p className="text-label text-sm">{session.location} • {session.lastActive}</p>
                              </div>
                            </div>
                            {!session.isCurrent && (
                              <button 
                                onClick={() => handleRevokeSession(session.id)}
                                className="text-red-400 hover:text-red-300 text-sm transition-colors"
                              >
                                {ts.security.disconnect}
                              </button>
                            )}
                            {session.isCurrent && (
                              <span className="text-accent-green text-sm font-medium">{ts.security.currentSession}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Passerelle SMS */}
            {activeTab === 'sms-gateway' && (
              <div className="space-y-6">
                {/* Intro */}
                <div className="card p-4 sm:p-6">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-xl bg-yellow-500/15 flex-shrink-0">
                      <Smartphone className="text-yellow-400" size={24} />
                    </div>
                    <div>
                      <h2 className="text-ink font-semibold text-lg mb-1">{ts.smsGateway.title}</h2>
                      <p className="text-label text-sm leading-relaxed">
                        {ts.smsGateway.description}
                      </p>
                    </div>
                  </div>

                  {/* Statut du token */}
                  <div className={`flex items-center gap-3 p-4 rounded-xl border mb-6 ${
                    gatewayInfo?.hasToken
                      ? 'bg-accent-green/10 border-accent-green/30'
                      : 'bg-yellow-500/10 border-yellow-500/30'
                  }`}>
                    <div className={`w-2.5 h-2.5 rounded-full ${gatewayInfo?.hasToken ? 'bg-accent-green' : 'bg-yellow-400'}`} />
                    <div className="min-w-0 flex-1">
                      {gatewayInfo?.hasToken ? (
                        <>
                          <p className="text-ink font-medium text-sm">{ts.smsGateway.tokenActive}</p>
                          <p className="text-label text-xs font-mono mt-0.5">{gatewayInfo.maskedToken}</p>
                        </>
                      ) : (
                        <p className="text-yellow-400 font-medium text-sm">{ts.smsGateway.noToken}</p>
                      )}
                    </div>
                    {gatewayInfo?.hasToken && (
                      <button
                        onClick={handleRevokeToken}
                        disabled={gatewayLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/15 text-red-400 border border-red-500/30 rounded-lg text-xs font-medium hover:bg-red-500/25 transition-all disabled:opacity-50"
                      >
                        <Trash size={13} />
                        {ts.smsGateway.revoke}
                      </button>
                    )}
                  </div>

                  {/* Bouton générer */}
                  <button
                    onClick={handleGenerateToken}
                    disabled={gatewayLoading}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 mb-4"
                    style={{
                      background: 'rgb(var(--c-accent-green-rgb))',
                      color: '#fff',
                      boxShadow: '0 4px 16px rgba(var(--c-accent-green-rgb), 0.35)',
                    }}
                  >
                    {gatewayLoading ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <RefreshCw size={16} />
                    )}
                    {gatewayInfo?.hasToken ? ts.smsGateway.regenerate : ts.smsGateway.generate}
                  </button>

                  {/* Token en clair — affiché UNE SEULE fois */}
                  {newToken && (
                    <div className="rounded-xl p-4 mb-6" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid rgba(var(--c-accent-green-rgb), 0.4)' }}>
                      <div className="flex items-center gap-2 mb-3">
                        <EyeOff size={14} className="text-accent-green" />
                        <p className="text-accent-green text-xs font-semibold uppercase tracking-wider">
                          {ts.smsGateway.tokenNote}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-2.5">
                        <code className="text-accent-green text-xs font-mono flex-1 break-all select-all">
                          {newToken}
                        </code>
                        <button
                          onClick={() => copyToClipboard(newToken, setTokenCopied)}
                          className="flex-shrink-0 p-1.5 hover:bg-accent-green/10 rounded-lg transition-all"
                          title="Copier"
                        >
                          {tokenCopied ? <Check size={15} className="text-accent-green" /> : <Copy size={15} className="text-label" />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* URL Webhook */}
                  {gatewayInfo?.webhookUrl && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-ink mb-2 flex items-center gap-2">
                        <Link size={14} className="text-label" />
                        {ts.smsGateway.webhookUrl}
                      </label>
                      <div className="flex items-center gap-2 rounded-lg px-3 py-2.5" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                        <code className="text-label text-xs font-mono flex-1 break-all">
                          {gatewayInfo.webhookUrl}
                        </code>
                        <button
                          onClick={() => copyToClipboard(gatewayInfo.webhookUrl, setUrlCopied)}
                          className="flex-shrink-0 p-1.5 hover:bg-accent-green/10 rounded-lg transition-all"
                          title="Copier l'URL"
                        >
                          {urlCopied ? <Check size={15} className="text-accent-green" /> : <Copy size={15} className="text-label" />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Guide de configuration Android */}
                <div className="card p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-ink mb-4 flex items-center gap-2">
                    <Smartphone size={18} className="text-label" />
                    {ts.smsGateway.androidTitle}
                  </h3>
                  <ol className="space-y-3">
                    {ts.smsGateway.steps.map(({ t: stepText, sub }, idx) => (
                      <li key={idx} className="flex gap-4">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-green/15 text-accent-green text-xs font-bold flex items-center justify-center mt-0.5">
                          {idx + 1}
                        </span>
                        <div>
                          <p className="text-ink text-sm">{stepText}</p>
                          {sub && <p className="text-label text-xs font-mono mt-0.5 px-2 py-1 rounded-md inline-block" style={{ background: 'var(--c-primary-dark)' }}>{sub}</p>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}

            {/* Apparence */}
            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <div className="card p-4 sm:p-6">
                  <h2 className="text-ink font-semibold text-lg mb-6">{ts.appearance.title}</h2>
                  
                  {/* Thème — branché sur ThemeContext (localStorage, instantané, persistant) */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-ink mb-3">{ts.appearance.theme}</label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => { setTheme('dark'); setSaveSuccess(ts.appearance.darkSuccess); }}
                        className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                          theme === 'dark'
                            ? 'border-accent-green bg-accent-green/10'
                            : 'border-primary-dark hover:border-accent-green/50'
                        }`}
                      >
                        <Moon size={20} />
                        <span className="text-ink">{ts.appearance.dark}</span>
                      </button>
                      <button
                        onClick={() => { setTheme('light'); setSaveSuccess(ts.appearance.lightSuccess); }}
                        className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                          theme === 'light'
                            ? 'border-accent-green bg-accent-green/10'
                            : 'border-primary-dark hover:border-accent-green/50'
                        }`}
                      >
                        <Sun size={20} />
                        <span className="text-ink">{ts.appearance.light}</span>
                      </button>
                      <button
                        onClick={() => { setTheme('system'); setSaveSuccess(ts.appearance.autoSuccess); }}
                        className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                          theme === 'system'
                            ? 'border-accent-green bg-accent-green/10'
                            : 'border-primary-dark hover:border-accent-green/50'
                        }`}
                      >
                        <Monitor size={20} />
                        <span className="text-ink">{ts.appearance.auto}</span>
                      </button>
                    </div>
                  </div>

                  {/* Taille de police */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-ink mb-3">{ts.appearance.fontSize}</label>
                    <div className="grid grid-cols-3 gap-3">
                      <button className="p-3 text-ink rounded-lg transition-all" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                        {ts.appearance.small}
                      </button>
                      <button className="p-3 bg-accent-green/10 text-accent-green rounded-lg border border-accent-green/30">
                        {ts.appearance.medium}
                      </button>
                      <button className="p-3 text-ink rounded-lg transition-all" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                        {ts.appearance.large}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Données */}
            {activeTab === 'data' && (
              <div className="space-y-6">
                <div className="card p-4 sm:p-6">
                  <h2 className="text-ink font-semibold text-lg mb-6">{ts.data.title}</h2>
                  
                  <div className="space-y-6">
                    {/* Export */}
                    <div>
                      <h3 className="text-ink font-semibold mb-4">{ts.data.exportTitle}</h3>
                      <p className="text-label mb-4">{ts.data.exportDesc}</p>
                      <button
                        onClick={handleExportData}
                        disabled={loading}
                        className="px-4 py-2 bg-accent-green text-black rounded-lg font-medium hover:bg-accent-green/80 transition-all disabled:opacity-50 flex items-center gap-2"
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                        ) : (
                          <Download size={16} />
                        )}
                        {ts.data.export}
                      </button>
                    </div>

                    {/* Suppression */}
                    <div>
                      <h3 className="text-ink font-semibold mb-4">{ts.data.deleteTitle}</h3>
                      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="text-red-500 mt-1" size={20} />
                          <div>
                            <p className="text-ink font-medium mb-2">{ts.data.deleteWarning}</p>
                            <p className="text-label text-sm mb-3">{ts.data.deleteDesc}</p>
                            <ul className="text-label text-sm space-y-1 list-disc list-inside">
                              {ts.data.deleteItems.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                      <button 
                        onClick={handleDeleteAccount}
                        disabled={loading}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-all disabled:opacity-50 flex items-center gap-2"
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <Trash2 size={16} />
                        )}
                        {ts.data.deleteBtn}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Aide */}
            {activeTab === 'help' && (
              <div className="space-y-6">
                <div className="card p-4 sm:p-6">
                  <h2 className="text-ink font-semibold text-lg mb-6">{ts.help.title}</h2>
                  
                  <div className="space-y-4">
                    {ts.help.faq.map((item, i) => (
                      <div key={i} className="p-4 rounded-lg cursor-pointer" style={{ background: 'var(--c-hover)' }}>
                        <h3 className="text-ink font-medium mb-2">{item.q}</h3>
                        <p className="text-label text-sm">{item.a}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex gap-4">
                    <button
                      onClick={() => window.open('mailto:support@swift-ai.com?subject=Support Request', '_blank')}
                      className="flex-1 px-4 py-2 bg-accent-green text-black rounded-lg font-medium hover:bg-accent-green/80 transition-all"
                    >
                      {ts.help.contactSupport}
                    </button>
                    <button
                      onClick={() => window.open('https://docs.swift-ai.com', '_blank')}
                      className="flex-1 px-4 py-2 text-ink rounded-lg font-medium transition-all" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                    >
                      {ts.help.visitDocs}
                    </button>
                  </div>

                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
